import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatChipInputEvent } from '@angular/material/chips';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterFieldItem } from '@ukam/core/filters/models/filter-field-item';

interface StringFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

const STRING_FILTER_OPERATORS: StringFilterOperatorOption[] = [
  { value: 'regex', label: 'Contains' },
  { value: 'eq', label: 'Equal to' },
  { value: 'ne', label: 'Not equal to' },
  { value: 'st', label: 'Starts with' },
  { value: 'ed', label: 'Ends with' },
  { value: 'nst', label: 'Does not start with' },
  { value: 'ned', label: 'Does not end with' },
  { value: 'in', label: 'In' },
  { value: 'nin', label: 'Not in' },
];

@Component({
  selector: 'app-string-filter',
  templateUrl: './string-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppStringFilterComponent), multi: true },
  ],
})
export class AppStringFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = STRING_FILTER_OPERATORS;
  separatorKeys = [ENTER, COMMA];

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;
  value = '';
  items: FilterFieldItem[] = [];

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(800)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get isList(): boolean {
    return this.operator === 'in' || this.operator === 'nin';
  }

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    this.operator = op;
    this.value = '';
    this.items = [];
    this.emit({ operator: op, value: undefined, items: [] });
  }

  onValueChange(value: string): void {
    this.value = value;
    this.emit({ value: value || undefined });
  }

  onChipInputTokenEnd(event: MatChipInputEvent): void {
    this.addItem(Number(event.value));
    event.chipInput?.clear();
  }

  removeItem(index: number): void {
    this.items = this.items.filter((_, i) => i !== index);
    this.emit({ items: this.items });
  }

  clear(): void {
    this.value = '';
    this.items = [];
    this.emit({ value: undefined, items: [] });
  }

  private addItem(code: number): void {
    if (!code || this.items.some(i => i.code === code)) return;

    this.items = [...this.items, { code: code, name: code.toString(), selected: true }];
    this.emit({ items: this.items });
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;
    this.value = this.field?.value ?? '';
    this.items = this.field?.items ?? [];
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
